import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuthenticatedFetch } from "@/hooks/use-authenticated-fetch";
import { ApiError } from "@/lib/api";
import { autodeskKeys } from "@/lib/query-keys";
import type {
  AutodeskConnection,
  AutodeskConnectionPayload,
  RecordAutodeskPush,
} from "@/types/api";

/**
 * The PUT endpoint behaves PATCH-like server-side: an omitted optional field
 * is preserved on update, an explicit `null` clears it. The project-settings
 * panel relies on this deliberately — it sends only `forma_hub_id`,
 * `forma_project_id`, `is_active`, and OMITS `default_subtype_id`/
 * `default_assignee_id` entirely, so a save never clears a subtype/assignee
 * configured some other way (ops-set today; a future connect-time
 * auto-resolve tomorrow). A caller that genuinely wants to clear one of
 * those fields must pass `null` for it explicitly.
 *
 * Saving a connection can flip which Forma projects are reachable (a new
 * hub/project pairing), so the forma-projects listing is invalidated
 * alongside the connection itself.
 *
 * `onSuccess` writes the response straight into the connection query's cache
 * synchronously — the panel's picker collapses immediately on success (see
 * autodesk-section.tsx), but without this write it would briefly re-render
 * against the stale (pre-connect) cached value until the `onSettled`
 * invalidation's refetch resolves, showing "Connect Autodesk" again for as
 * long as that request takes. The invalidation below is kept as-is so the
 * cache still gets reconciled against the server's actual state.
 */
export function useSaveAutodeskConnection(projectId: string) {
  const fetchWithAuth = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: AutodeskConnectionPayload) =>
      fetchWithAuth<AutodeskConnection>(
        `/projects/2.0/projects/${projectId}/autodesk-connection/`,
        { method: "PUT", body: JSON.stringify(payload) },
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(autodeskKeys.connection(projectId), data);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: autodeskKeys.connection(projectId),
      });
      void queryClient.invalidateQueries({
        queryKey: autodeskKeys.formaProjects(projectId),
      });
    },
  });
}

/**
 * `onSuccess` writes `null` into the connection query's cache synchronously
 * for the same reason `useSaveAutodeskConnection` does: without it, the
 * connected view lingers on stale cached data until the `onSettled`
 * refetch resolves, and a second click during that window would DELETE a
 * connection that's already gone server-side.
 */
export function useDeleteAutodeskConnection(projectId: string) {
  const fetchWithAuth = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      fetchWithAuth<void>(
        `/projects/2.0/projects/${projectId}/autodesk-connection/`,
        { method: "DELETE" },
      ),
    onSuccess: () => {
      queryClient.setQueryData(autodeskKeys.connection(projectId), null);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: autodeskKeys.connection(projectId),
      });
      void queryClient.invalidateQueries({
        queryKey: autodeskKeys.formaProjects(projectId),
      });
    },
  });
}

/**
 * POST reserves + dispatches a push. A 409 means a push row already exists
 * (in flight or completed) — the backend returns the EXISTING row as the
 * response body in that case, not just an error detail. That row is real
 * data the panel needs (to show "already pushed" / current status), so it's
 * caught here and resolved as the mutation's success value instead of
 * rethrown. Any other error (400 no-connection/no-photos, 403 not-viewable)
 * propagates normally.
 *
 * `onSuccess` writes the resolved row (the 202's fresh `pending` row, or the
 * resolved-409's existing row) straight into the push query's cache
 * synchronously. Without this, the button's `useRecordAutodeskPush` read
 * stays on its pre-click state ("Send to Autodesk", clickable) until the
 * `onSettled` invalidation's refetch resolves — a real window, not a
 * theoretical one, where the button can be tapped again mid-flight. The
 * invalidation below is kept as-is so the cache still reconciles against
 * the server's actual (possibly already-advanced, e.g. by the poll) state.
 */
export function useSendRecordToAutodesk(projectId: string, recordId: string) {
  const fetchWithAuth = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      try {
        return await fetchWithAuth<RecordAutodeskPush>(
          `/projects/2.0/projects/${projectId}/records/${recordId}/autodesk-push/`,
          { method: "POST" },
        );
      } catch (err) {
        if (err instanceof ApiError && err.status === 409) {
          return err.body as RecordAutodeskPush;
        }
        throw err;
      }
    },
    onSuccess: (data) => {
      queryClient.setQueryData(
        autodeskKeys.recordPush(projectId, recordId),
        data,
      );
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: autodeskKeys.recordPush(projectId, recordId),
      });
    },
  });
}
